import { useContext, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MaskedLine, Reveal } from "@/components/Motion";
import { LenisContext } from "@/App";
import { products } from "@/data/products";

const NOTES = [
  {
    title: "Traditional use, honestly told",
    text: "Every description on this site refers to traditional herbal use and general wellness context. Where a benefit is described, it reflects how the herb has historically been used in herbal practice, not a medical claim.",
  },
  {
    title: "Gentle by design",
    text: "Our formulas favour adaptogenic, calming and nourishing herbs over stimulant-driven effects. The intention is steady, balanced support rather than sharp peaks.",
  },
  {
    title: "Safety notices are part of the craft",
    text: "Products that carry specific cautions, such as the Smoking Blend and the Yoni Steam, display them prominently. Honest herbalism includes honest risk.",
  },
];

export default function Info() {
  const { hash } = useLocation();
  const lenisRef = useContext(LenisContext);

  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => lenisRef.current?.scrollTo(hash, { offset: -60 }), 150);
      return () => clearTimeout(t);
    }
  }, [hash, lenisRef]);

  const herbIndex = useMemo(() => {
    const map = new Map();
    products.forEach((p) => {
      p.ingredients.forEach((ing) => {
        if (!map.has(ing.name)) {
          map.set(ing.name, { name: ing.name, text: ing.text, foundIn: [p.name] });
        } else {
          const entry = map.get(ing.name);
          if (!entry.foundIn.includes(p.name)) entry.foundIn.push(p.name);
        }
      });
    });
    return [...map.values()];
  }, []);

  return (
    <main data-testid="info-page" className="pt-24">
      <header className="mx-auto max-w-[1600px] px-6 pb-24 pt-16 lg:px-12 lg:pt-24">
        <Reveal y={16}>
          <p className="mb-8 text-xs uppercase tracking-[0.4em] text-gilt">Information & Reference</p>
        </Reveal>
        <h1 className="font-serif text-6xl leading-[0.98] text-ivory lg:text-8xl" data-testid="info-headline">
          <MaskedLine delay={0.1}>The Herbal</MaskedLine>
          <MaskedLine delay={0.25} className="italic text-gilt">Index.</MaskedLine>
        </h1>
        <Reveal delay={0.4}>
          <p className="mt-10 max-w-xl text-base leading-relaxed text-sand">
            A reference for every herb across the Faeloria collection: its
            traditional role, and the formulations where you will find it.
          </p>
        </Reveal>
      </header>

      <section data-testid="herb-index" className="mx-auto max-w-[1600px] px-6 pb-28 lg:px-12">
        <Reveal>
          <p className="mb-10 text-xs uppercase tracking-[0.4em] text-gilt">
            {herbIndex.length} Herbs
          </p>
        </Reveal>
        <div className="border-t border-moss-700">
          {herbIndex.map((h, i) => (
            <Reveal key={h.name} delay={Math.min(i * 0.03, 0.2)}>
              <div data-testid={`herb-entry-${i}`} className="grid gap-4 border-b border-moss-700 py-10 lg:grid-cols-12">
                <div className="flex items-baseline gap-4 lg:col-span-4">
                  <span className="font-serif text-sm italic text-gilt">{String(i + 1).padStart(2, "0")}</span>
                  <h2 className="font-serif text-3xl italic text-ivory">{h.name}</h2>
                </div>
                <p className="text-sm leading-relaxed text-sand lg:col-span-5">{h.text}</p>
                <div className="lg:col-span-3">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-sand/60">Found in</p>
                  <ul className="mt-3 space-y-2">
                    {h.foundIn.map((f) => (
                      <li key={f} className="text-sm text-ivory">{f}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="info-notes" className="border-t border-moss-700/60 bg-moss-900">
        <div className="mx-auto max-w-[1600px] px-6 py-28 lg:px-12">
          <Reveal>
            <div id="safety" data-testid="info-disclaimer" className="scroll-mt-28 border border-giltdeep/60 p-10 lg:p-20">
              <p className="text-sm uppercase tracking-[0.35em] text-gilt">General Disclaimer</p>
              <p className="mt-8 max-w-5xl font-serif text-2xl font-medium leading-snug text-ivory lg:text-4xl">
                Traditional herbal uses and general wellness benefits described across this
                site are provided for informational and educational purposes only. Individual
                responses to herbs can vary. Faeloria Collective products are not intended to
                diagnose, treat, cure, or prevent any disease.
              </p>
              <p className="mt-8 max-w-4xl text-base leading-relaxed text-sand lg:text-lg">
                Always consult a qualified healthcare practitioner before beginning any
                herbal regimen, particularly if you are pregnant, nursing, taking
                medication, or managing a health condition.
              </p>
            </div>
          </Reveal>

          <Reveal>
            <p className="mb-10 mt-24 text-xs uppercase tracking-[0.4em] text-gilt">How to Read Our Blends</p>
          </Reveal>
          <div className="grid gap-px border border-moss-700 bg-moss-700 lg:grid-cols-2">
            {NOTES.map((n, i) => (
              <Reveal key={n.title} delay={i * 0.06} className={NOTES.length % 2 === 1 && i === NOTES.length - 1 ? "lg:col-span-2" : ""}>
                <div data-testid={`info-note-${i + 1}`} className="h-full bg-moss-950 p-10 transition-colors duration-700 hover:bg-moss-900 lg:p-12">
                  <p className="font-serif text-lg italic text-gilt">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-4 font-serif text-2xl text-ivory">{n.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-sand">{n.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <Link
              data-testid="info-collection-link"
              to="/"
              className="group mt-16 inline-flex items-center gap-4 border border-gilt px-8 py-4 text-xs uppercase tracking-[0.3em] text-ivory transition-colors duration-500 hover:bg-gilt hover:text-[#EFE8D2]"
            >
              Return to the Collection
              <ArrowUpRight size={14} className="transition-transform duration-500 group-hover:-translate-y-1 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
